import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { type ReactNode, useEffect, useRef } from 'react';

import SpaceY from '../../components/SpaceY';

const crossGray300Icon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/svg/cross-gray-300-icon_KZBSoO6xj.svg`;
const confirmGray300Icon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/svg/confirm-gray-300-icon_aDjiy_98E.svg`;

export interface AlertModalProps {
  open: boolean;
  onClose: (() => void) | (() => Promise<void>);
  // 제목
  title?: string;
  titleClassName?: string;
  haederContent?: React.ReactNode;
  hasConfirmIcon?: boolean;
  // 내용
  content: React.ReactNode | string;
  // 버튼
  hasButtonGroup?: boolean;
  // 확인 버튼
  buttonText?: React.ReactNode | string;
  onComplete?: (() => void) | (() => Promise<void>);
  completeButtonColor?: 'bg-gray-300' | 'bg-soil-brown-700' | 'bg-brand-1';
  // 취소 버튼
  hasCancelButton?: boolean;
  onCancel?: (() => void) | (() => Promise<void>);
  cancelButtonText?: string;
  cancelButtonFlexBasis?: string;
  cancelButtonColor?: 'bg-gray-100' | 'bg-gray-600';
  cancelButtonTextColor?: 'text-gray-600' | 'text-white';
  // 닫기 버튼
  hasCloseButton?: boolean;
  // 기타
  zIndex?: number;
  // onComplete시 자동으로 close되는 것 방지
  preventOnCompleteClose?: boolean;
  // onCancel시 자동으로 close되는 것 방지
  preventOnCancelClose?: boolean;
  className?: string;
  backgroundColor?: 'bg-brand-green-10' | 'bg-brand-16';
  bottomChildren?: ReactNode;
  disabledComplete?: boolean;
  // image를 넣는경우 default padding제거
  zeroPadding?: boolean;
  StyledDialog?: {
    maxWidth?: '343px' | '448px';
    backgroundColor?: 'transparent';
    Title?: {
      backgroundColor?: '#fff';
    };
    Content?: {
      padding?: string;
      backgroundColor?: '#fff';
    };
  };
}

/**
 * @link https://mui.com/material-ui/api/dialog/
 */
const Styled = {
  Dialog: styled(Dialog, {
    shouldForwardProp: (prop) =>
      prop !== 'customMaxWidth' && prop !== 'backgroundColor',
  })(
    ({
      customMaxWidth,
      backgroundColor,
    }: {
      customMaxWidth: '343px' | '448px' | undefined;
      backgroundColor: 'transparent' | undefined;
    }) => ({
      '& .MuiDialog-paper': {
        margin: '32px 16px',
        width: '100%',
        maxWidth: customMaxWidth,
        backgroundColor,
        boxShadow: 'none',
      },
    }),
  ),
};

const AlertModal = ({
  open,
  onClose,
  title,
  titleClassName = '',
  haederContent,
  hasConfirmIcon = false,
  content,
  hasButtonGroup = true,
  buttonText = '확인',
  onComplete = () => {},
  completeButtonColor = 'bg-soil-brown-700',
  hasCancelButton = false,
  onCancel = () => {},
  cancelButtonText = '취소',
  cancelButtonFlexBasis,
  cancelButtonColor = 'bg-gray-100',
  cancelButtonTextColor = 'text-gray-600',
  hasCloseButton = true,
  zIndex = 5000,
  preventOnCompleteClose = false,
  preventOnCancelClose = false,
  className,
  backgroundColor,
  bottomChildren = <></>,
  disabledComplete = false,
  zeroPadding = false,
  StyledDialog = { maxWidth: '448px' },
}: AlertModalProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  /** unmount시 body scroll lock 초기화 */
  useEffect(() => {
    return () => {
      document.body.style.cssText = '';
    };
  }, []);

  return (
    <>
      <Styled.Dialog
        id="mui-dialog-alert-modal"
        ref={modalRef}
        open={open}
        onClick={(e) => e.stopPropagation()}
        onClose={onClose}
        scroll={'paper'}
        style={{ zIndex }}
        className={className}
        customMaxWidth={StyledDialog.maxWidth}
        backgroundColor={StyledDialog.backgroundColor}
      >
        <DialogTitle
          className={`relative font-pretendard ${backgroundColor} ${
            zeroPadding && 'zero-padding'
          }`}
          sx={StyledDialog.Title}
        >
          {!!title && (
            <>
              <SpaceY height={22} />
              <p
                role="heading"
                aria-level={5}
                className="font-h3 flex items-start gap-6px font-bold leading-1 text-gray-700"
              >
                {hasConfirmIcon && <img src={confirmGray300Icon} alt="" />}
                <span
                  data-testid="alert-modal-title"
                  className={`relative top-3px whitespace-pre-line ${titleClassName}`}
                >
                  {title}
                </span>
              </p>
            </>
          )}
          {haederContent && <div className="mt-12px">{haederContent}</div>}

          {hasCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-16px right-16px z-[1] p-4px"
              type="button"
            >
              <img src={crossGray300Icon} alt="" />
            </button>
          )}
        </DialogTitle>
        <DialogContent
          className={`${backgroundColor} ${zeroPadding && 'zero-padding'}`}
          sx={StyledDialog.Content}
        >
          <div
            data-testid="alert-modal-content"
            className="whitespace-pre-line font-pretendard"
          >
            {content}
          </div>
        </DialogContent>
        {hasButtonGroup && (
          <DialogActions>
            <div className="flex w-full font-pretendard">
              {hasCancelButton && (
                <button
                  onClick={() => {
                    onCancel();
                    preventOnCancelClose || onClose();
                  }}
                  data-testid="alert-modal-cancel-button"
                  className={`flex-shrink-0 text-16px ${cancelButtonColor} ${cancelButtonTextColor}`}
                  style={{ flex: `1 0 ${cancelButtonFlexBasis ?? '34%'}` }}
                  type="button"
                >
                  {cancelButtonText}
                </button>
              )}
              <button
                disabled={disabledComplete}
                onClick={() => {
                  onComplete();
                  preventOnCompleteClose || onClose();
                }}
                data-testid="alert-modal-confirm-button"
                className={`flex-center h-54px w-full text-16px font-semibold text-white transition disabled:bg-gray-300 ${completeButtonColor}`}
                type="button"
              >
                {buttonText}
              </button>
            </div>
          </DialogActions>
        )}
        {bottomChildren}
      </Styled.Dialog>
    </>
  );
};

export default AlertModal;
